import { z } from 'zod';

import { assertOk, parseJson } from './response';

type ApiFetchFn = typeof import('./requests')['apiFetch'];

const resolveApiFetch = async (): Promise<ApiFetchFn> => {
  const globalRef = globalThis as typeof globalThis & {
    jest?: {
      requireMock?: (id: string) => unknown;
    };
  };

  const jestGlobal = globalRef.jest;
  if (jestGlobal && typeof jestGlobal.requireMock === 'function') {
    try {
      const mockedModule = jestGlobal.requireMock('@/lib/api/requests') as { apiFetch?: ApiFetchFn };
      if (mockedModule && typeof mockedModule.apiFetch === 'function') {
        return mockedModule.apiFetch;
      }
    } catch (error) {
      void error;
    }
  }

  const module = await import('./requests');
  return module.apiFetch;
};

type HttpMethod = 'GET' | 'POST' | 'PATCH';

type RequestOptions<T> = {
  method?: HttpMethod;
  body?: unknown;
  signal?: AbortSignal;
  tenantSlug: string;
  headers?: Record<string, string>;
  schema: z.ZodType<T>;
};

const hasHeader = (headers: Record<string, string>, name: string): boolean =>
  Object.keys(headers).some((key) => key.toLowerCase() === name.toLowerCase());

const serialiseBody = (body: unknown): BodyInit | undefined => {
  if (body === undefined || body === null) {
    return undefined;
  }
  if (typeof body === 'string') {
    return body;
  }
  if (body instanceof URLSearchParams) {
    return body;
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return body;
  }
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return body;
  }
  return JSON.stringify(body);
};

const normaliseNumberInput = (value: unknown): unknown => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return undefined;
    }
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
    return value;
  }
  if (typeof value === 'bigint') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
    return value;
  }
  return value;
};

const numberField = () => z.preprocess(normaliseNumberInput, z.number());
const numberFieldOptional = () => z.preprocess(normaliseNumberInput, z.number().optional());
const numberFieldNullable = () => z.preprocess(normaliseNumberInput, z.number().nullable());
const numberFieldOptionalNullable = () =>
  z.preprocess(normaliseNumberInput, z.number().nullable().optional());

const loyaltyStateSchema = z.enum(['unknown', 'active', 'lapsed', 'vip', 'suspended']);

export const CustomerContactMethodSchema = z.object({
  id: z.string(),
  type: z.enum(['email', 'sms', 'phone', 'push', 'card_fingerprint', 'app', 'web', 'other']),
  label: z.string().nullable().optional(),
  value: z.string(),
  normalizedValue: z.string().nullable().optional(),
  status: z.enum(['unverified', 'pending', 'verified', 'bounced', 'suppressed']),
  isPrimary: z.boolean().optional(),
  consentedAt: numberFieldOptionalNullable(),
  consentVersion: z.string().nullable().optional(),
  lastVerifiedAt: numberFieldOptionalNullable(),
  lastFailedAt: numberFieldOptionalNullable(),
  metadata: z.record(z.unknown()).nullable().optional(),
  updatedAt: numberFieldOptionalNullable(),
});

export const CustomerPreferenceSchema = z
  .object({
    id: z.string(),
    kind: z.string(),
    key: z.string(),
    value: z.unknown(),
    confidence: numberFieldOptionalNullable(),
    weight: numberFieldOptionalNullable(),
    source: z.string().nullable().optional(),
    lastSeenAt: numberFieldOptionalNullable(),
    expiresAt: numberFieldOptionalNullable(),
    metadata: z.record(z.unknown()).nullable().optional(),
    createdAt: numberFieldOptionalNullable(),
    updatedAt: numberFieldOptionalNullable(),
  })
  .passthrough();

export const CustomerNoteSchema = z.object({
  id: z.string(),
  visibility: z.string(),
  authorType: z.string().nullable().optional(),
  authorId: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  body: z.string(),
  pinned: z.boolean(),
  orderId: z.string().nullable().optional(),
  contactId: z.string().nullable().optional(),
  outreachId: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
  createdAt: numberFieldOptionalNullable(),
  updatedAt: numberFieldOptionalNullable(),
});

async function requestJson<T>(
  path: string,
  { method = 'GET', body, signal, tenantSlug, headers = {}, schema }: RequestOptions<T>,
): Promise<T> {
  const apiFetch = await resolveApiFetch();
  const requestHeaders: Record<string, string> = {
    Accept: 'application/json',
    'X-Tenant': tenantSlug,
    ...headers,
  };

  const serialisedBody = serialiseBody(body);
  if (serialisedBody !== undefined && typeof serialisedBody === 'string' && !hasHeader(requestHeaders, 'content-type')) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  const response = await apiFetch(path, {
    method,
    body: serialisedBody,
    signal,
    headers: requestHeaders,
  });

  await assertOk(response);
  return parseJson(response, schema);
}

export type CustomerKind = 'manual' | 'order-derived';

export const CustomerSummarySchema = z
  .object({
    id: z.string(),
    name: z.string().nullable(),
    phone: z.string().nullable(),
    kind: z.enum(['manual', 'order-derived']).optional(),
    email: z.string().email().nullable().optional(),
    totalOrders: numberField(),
    totalSpentCents: numberFieldOptionalNullable(),
    averageOrderValueCents: numberFieldOptional(),
    avgOrderValueCents: numberFieldOptional(),
    lastVisit: z.string().nullable().optional(),
    firstVisit: z.string().nullable().optional(),
    lastOrderAt: numberFieldOptionalNullable(),
    firstOrderAt: numberFieldOptionalNullable(),
    orderType: z.enum(['dine-in', 'pickup']).optional(),
    dineInOrders: numberFieldOptional(),
    pickupOrders: numberFieldOptional(),
    preferredTable: numberFieldOptionalNullable(),
    currentTable: numberFieldOptionalNullable(),
    currency: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    punches: numberFieldOptional(),
    loyaltyPoints: numberFieldOptional(),
    favoriteItemIds: z.array(z.string()).optional(),
    guestExternalId: z.string().nullable().optional(),
    guestConsent: z.record(z.unknown()).nullable().optional(),
    scheduledPreferences: z.record(z.unknown()).nullable().optional(),
    visitCount: numberFieldOptional(),
    visitStreak: numberFieldOptional(),
    avgVisitIntervalMs: numberFieldOptionalNullable(),
    lifetimeValueCents: numberFieldOptionalNullable(),
    loyaltyTier: z.string().nullable().optional(),
    loyaltyState: loyaltyStateSchema.nullable().optional(),
    marketingOptIn: z.boolean().optional(),
    marketingOptInAt: numberFieldOptionalNullable(),
    marketingOptOutAt: numberFieldOptionalNullable(),
  })
  .passthrough();
export type CustomerSummary = z.infer<typeof CustomerSummarySchema>;
export type CustomerContactMethod = z.infer<typeof CustomerContactMethodSchema>;
export type CustomerPreference = z.infer<typeof CustomerPreferenceSchema>;
export type CustomerNote = z.infer<typeof CustomerNoteSchema>;

export const ConsentSnapshotSchema = z.object({
  id: z.string(),
  surface: z.string(),
  method: z.string(),
  payload: z.record(z.unknown()).default({}),
  recordedAt: numberFieldOptionalNullable(),
  recordedBy: z.string().nullable().optional(),
});
export type ConsentSnapshot = z.infer<typeof ConsentSnapshotSchema>;

export const CustomerOrderSchema = z
  .object({
    id: z.string(),
    status: z.string(),
    totalCents: numberFieldOptionalNullable(),
    tableNumber: numberFieldOptionalNullable(),
    createdAt: numberFieldOptionalNullable(),
    updatedAt: numberFieldOptionalNullable(),
  })
  .passthrough();
export type CustomerOrder = z.infer<typeof CustomerOrderSchema>;

export const CustomerDetailsSchema = CustomerSummarySchema.extend({
  avgOrderValueCents: numberField(),
  recentOrders: z.array(CustomerOrderSchema),
  visitCount: numberFieldOptional(),
  visitStreak: numberFieldOptional(),
  avgVisitIntervalMs: numberFieldOptionalNullable(),
  lifetimeValueCents: numberFieldOptionalNullable(),
  loyaltyTier: z.string().nullable().optional(),
  loyaltyState: loyaltyStateSchema.nullable().optional(),
  marketingOptIn: z.boolean().optional(),
  marketingOptInAt: numberFieldOptionalNullable(),
  marketingOptOutAt: numberFieldOptionalNullable(),
  contactMethods: z.array(CustomerContactMethodSchema).default([]),
  preferences: z.array(CustomerPreferenceSchema).default([]),
  notes: z.array(CustomerNoteSchema).default([]),
  consentSnapshots: z.array(ConsentSnapshotSchema).default([]),
  legacyNotes: z.string().nullable().optional(),
});
export type CustomerDetails = z.infer<typeof CustomerDetailsSchema>;

const CustomerStatsSchema = z.object({
  totalOrders: numberField(),
  totalSpentCents: numberField(),
  avgOrderValueCents: numberField(),
  lastOrderAt: numberFieldNullable(),
  firstOrderAt: numberFieldNullable(),
  completed: numberField(),
  cancelled: numberField(),
});
export type CustomerStats = z.infer<typeof CustomerStatsSchema>;

export interface ListCustomersParams {
  tenantSlug: string;
  limit?: number;
  offset?: number;
  search?: string;
  loyaltyTier?: string;
  loyaltyState?: z.infer<typeof loyaltyStateSchema>;
  signal?: AbortSignal;
}

export const ListCustomersResponseSchema = z.object({
  customers: z.array(CustomerSummarySchema),
});
export type ListCustomersResponse = z.infer<typeof ListCustomersResponseSchema>;

export async function listCustomers({ tenantSlug, limit, offset, search, loyaltyTier, loyaltyState, signal }: ListCustomersParams): Promise<ListCustomersResponse> {
  const params = new URLSearchParams();
  if (typeof limit === 'number') {
    params.set('limit', String(limit));
  }
  if (typeof offset === 'number') {
    params.set('offset', String(offset));
  }
  if (search && search.trim().length > 0) {
    params.set('search', search.trim());
  }
  if (loyaltyTier && loyaltyTier.trim().length > 0) {
    params.set('loyaltyTier', loyaltyTier.trim());
  }
  if (loyaltyState) {
    params.set('loyaltyState', loyaltyState);
  }

  const path = params.size > 0 ? `/customers?${params.toString()}` : '/customers';
  return requestJson(path, { tenantSlug, signal, schema: ListCustomersResponseSchema });
}

const CustomerSearchResultSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  phone: z.string().nullable(),
  lastOrderAt: z.number().nullable(),
  totalOrders: z.number(),
  kind: z.enum(['manual', 'order-derived']),
});
export type CustomerSearchResult = z.infer<typeof CustomerSearchResultSchema>;

export interface SearchCustomersParams {
  tenantSlug: string;
  query: string;
  signal?: AbortSignal;
}

const SearchCustomersResponseSchema = z.object({
  ok: z.literal(true),
  results: z.array(CustomerSearchResultSchema),
});
export type SearchCustomersResponse = z.infer<typeof SearchCustomersResponseSchema>;

export async function searchCustomers({ tenantSlug, query, signal }: SearchCustomersParams): Promise<SearchCustomersResponse> {
  const params = new URLSearchParams({ q: query });
  const payload = await requestJson(`/customers/search?${params.toString()}`, {
    tenantSlug,
    signal,
    schema: SearchCustomersResponseSchema,
  });
  return payload;
}

export interface GetCustomerDetailsParams {
  tenantSlug: string;
  customerId: string;
  signal?: AbortSignal;
}

const CustomerDetailsResponseSchema = z.object({
  ok: z.literal(true),
  customer: CustomerDetailsSchema,
});
export type CustomerDetailsResponse = z.infer<typeof CustomerDetailsResponseSchema>;

export async function getCustomerDetails({ tenantSlug, customerId, signal }: GetCustomerDetailsParams): Promise<CustomerDetailsResponse> {
  return requestJson(`/customers/${encodeURIComponent(customerId)}`, {
    tenantSlug,
    signal,
    schema: CustomerDetailsResponseSchema,
  });
}

const CustomerOrdersResponseSchema = z.object({
  ok: z.literal(true),
  orders: z.array(CustomerOrderSchema),
});
export type CustomerOrdersResponse = z.infer<typeof CustomerOrdersResponseSchema>;

export async function getCustomerOrders({ tenantSlug, customerId, signal }: GetCustomerDetailsParams): Promise<CustomerOrdersResponse> {
  return requestJson(`/customers/${encodeURIComponent(customerId)}/orders`, {
    tenantSlug,
    signal,
    schema: CustomerOrdersResponseSchema,
  });
}

const CustomerStatsResponseSchema = z.object({
  ok: z.literal(true),
  stats: CustomerStatsSchema,
});
export type CustomerStatsResponse = z.infer<typeof CustomerStatsResponseSchema>;

export async function getCustomerStats({ tenantSlug, customerId, signal }: GetCustomerDetailsParams): Promise<CustomerStatsResponse> {
  return requestJson(`/customers/${encodeURIComponent(customerId)}/stats`, {
    tenantSlug,
    signal,
    schema: CustomerStatsResponseSchema,
  });
}

const CreateCustomerInputSchema = z.object({
  name: z.string().min(1),
  phone: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  notes: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type CreateCustomerInput = z.infer<typeof CreateCustomerInputSchema>;

const CreateCustomerResponseSchema = z.object({
  ok: z.literal(true),
  customer: CustomerSummarySchema,
});
export type CreateCustomerResponse = z.infer<typeof CreateCustomerResponseSchema>;

export async function createCustomer(tenantSlug: string, input: CreateCustomerInput): Promise<CreateCustomerResponse> {
  const body = CreateCustomerInputSchema.parse(input);
  return requestJson('/customers', {
    method: 'POST',
    tenantSlug,
    body,
    schema: CreateCustomerResponseSchema,
  });
}

const UpdateCustomerInputSchema = z.object({
  name: z.string().optional(),
  phone: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  notes: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
});
export type UpdateCustomerInput = z.infer<typeof UpdateCustomerInputSchema>;

export type UpdateCustomerResponse = CreateCustomerResponse;

export async function updateCustomer(
  tenantSlug: string,
  customerId: string,
  input: UpdateCustomerInput,
): Promise<UpdateCustomerResponse> {
  const body = UpdateCustomerInputSchema.parse(input);
  return requestJson(`/customers/${encodeURIComponent(customerId)}`, {
    method: 'PATCH',
    tenantSlug,
    body,
    schema: CreateCustomerResponseSchema,
  });
}

const AddCustomerNoteResponseSchema = z.object({
  ok: z.literal(true),
  note: z.string(),
});
export type AddCustomerNoteResponse = z.infer<typeof AddCustomerNoteResponseSchema>;

export async function addCustomerNote(
  tenantSlug: string,
  customerId: string,
  note: string,
): Promise<AddCustomerNoteResponse> {
  return requestJson(`/customers/${encodeURIComponent(customerId)}/notes`, {
    method: 'POST',
    tenantSlug,
    body: { note },
    schema: AddCustomerNoteResponseSchema,
  });
}

const DirectOutreachResponseSchema = z.object({
  ok: z.literal(true),
  sent: z.number(),
  failed: z.number(),
  failures: z
    .array(
      z.object({
        profileId: z.string(),
        reason: z.string(),
      }),
    )
    .optional(),
});
export type DirectOutreachResponse = z.infer<typeof DirectOutreachResponseSchema>;

export const sendDirectCustomerOutreach = async ({
  tenantSlug,
  profileIds,
  channel,
  message,
  subject,
}: {
  tenantSlug: string;
  profileIds: string[];
  channel: 'email' | 'sms';
  message: string;
  subject?: string;
}): Promise<DirectOutreachResponse> =>
  requestJson('/customers/outreach/direct', {
    method: 'POST',
    tenantSlug,
    body: {
      profileIds,
      channel,
      message,
      subject,
    },
    schema: DirectOutreachResponseSchema,
  });
